import React, { useCallback, useState } from 'react';
import { Card, Button, Space, Tag, Typography, Spin, Empty, message, Avatar, Rate } from 'antd';
import { UserOutlined, ArrowRightOutlined, CloudDownloadOutlined, LockOutlined, UnlockOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@src/app/store';
import {
  setSelectedSnippet,
  setCurrentView,
  updateSearchConfig,
  updateSnippetInResults,
} from '@src/app/reducers/snippets/snippetsSlice';
import useTranslation from '@hooks/useTranslation';
import { UISnippet } from '@src/app/reducers/snippets/snippetsSlice';
import styles from './SnippetsList.module.css';
import SvgFromString from '@components/SvgFromString/SvgFromString';
import capitalize from 'capitalize';
import { ReactComponent as OrganizationIcon } from '@src/assets/img/menus/organization-info.svg';
import StyledButton from '@components/form-control/styledButton/styledButton';
import Snippetify, { DropCoordinates } from '@components/Snippetify/Snippetify';
import { getSnippetContents, rateSnippet } from '@src/api/snippet/snippetApi';

const { Text, Paragraph } = Typography;

const VerticalSeparator = () => {
  return (
    <div className={styles.VerticalSeparator} />
  );
};

/**
 * Formats a number with thousand separators
 * @param num - The number to format
 * @returns Formatted string with commas
 */
const formatDownloadCount = (num: number): string => {
  return num.toLocaleString();
};

type SnippetsListProps = {
  /** Callback fired when a snippet starts or stops being dragged */
  onSnippetDragging?: (isDragging: boolean) => void;
}

/**
 * Component for displaying a list of snippets with search results
 * @returns React.JSX.Element
 */
const SnippetsList = (props: SnippetsListProps): React.JSX.Element => {
  const { onSnippetDragging } = props;
  const dispatch = useDispatch();
  const t = useTranslation('Snippets.Drawer');
  const cw = t.withBaseKey('CommonWords');
  
  const {
    searchConfig,
    searchResults,
    totalResults,
    hasMore,
    isLoading,
    error,
  } = useSelector((state: RootState) => state.snippets);

  const [loadingMore, setLoadingMore] = useState(false);

  /**
   * Loads more snippets for pagination
   */
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) { return; }
    
    setLoadingMore(true);
    dispatch(updateSearchConfig({ page: (searchConfig.page || 1) + 1 }));
  }, [dispatch, searchConfig.page, hasMore, loadingMore]);

  /**
   * Handles rating a snippet
   */
  const handleRating = useCallback(async (snippetId: string, rating: number) => {
    try {
      const response = await rateSnippet(snippetId, {
        snippetId,
        rating,
      });
      
      dispatch(updateSnippetInResults({
        snippetId,
        updates: {
          rating: {
            average: response.average,
            total: response.total,
          },
        },
      }));
      
      message.success(t('Card.RatingSuccess', 'Rating updated successfully!'));
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || t('Card.RatingError', 'Failed to update rating');
      message.error(errorMessage);
    }
  }, [dispatch, t]);

  /**
   * Handles clicking on a snippet to view details
   */
  const handleViewDetails = useCallback((snippet: UISnippet) => {
    dispatch(setSelectedSnippet(snippet));
    dispatch(setCurrentView('detail'));
  }, [dispatch]);

  /**
   * Fetches the actual snippet contents before dropping
   */
  const handleOnBeforeDrop = useCallback(async (coordinates: DropCoordinates, meta: UISnippet) => {
    const snippetCode = await getSnippetContents(meta.id, meta.version);
    return {
      snippetCode,
    };
  }, []);

  /**
   * Handles the dragging state change for snippets
   * @param isDragging - Whether a snippet is currently being dragged
   */
  const handleIsDragging = useCallback((isDragging: boolean) => {
    onSnippetDragging?.(isDragging);
  }, [onSnippetDragging]);

  /**
   * Gets the visibility tag information based on the current visibility setting
   * @returns Object with visibility text, color, and icon
   */
  const getVisibilityTag = useCallback((visibility: string) => {
    switch (visibility) {
      case 'public':
        return {
          text: t('Card.Visibility.Public'),
          color: 'green',
          icon: <UnlockOutlined />,
        };
      case 'private':
        return {
          text: t('Card.Visibility.Private'),
          color: 'orange',
          icon: <LockOutlined />,
        };
      case 'organization':
        return {
          text: t('Card.Visibility.Organization'),
          color: 'blue',
          icon: <OrganizationIcon style={{ width: 12, height: 12, marginRight: 4 }} />,
        };
      default:
        return {
          text: t('Card.Visibility.Public'),
          color: 'green',
          icon: <UnlockOutlined />,
        };
    }
  }, [t]);

  const renderSnippetCard = (snippet: UISnippet) => {
    const visibilityTag = getVisibilityTag(snippet.visibility);
    
    const customIcon = snippet.iconSvg ? (
      <SvgFromString 
        svgStr={snippet.iconSvg} 
        width={55} 
        height={55}
      />
    ) : (
      <div className={styles.DefaultIcon}>
        <Text strong>{snippet.title.charAt(0).toUpperCase()}</Text>
      </div>
    );

    return (
      <Card
        key={snippet.id}
        className={styles.SnippetCard}
        hoverable
        classNames={{
          // body: styles.CardBody,
        }}
        styles={{
          body: {
            padding: '12px',
          }
        }}
      >
        <div className={styles.CardContent}>
          <div className={styles.DraggableContent}>
            <Snippetify 
              // the contents will be downloaded when dropping
              snippetCode={''} 
              meta={snippet} 
              onBeforeDrop={handleOnBeforeDrop}
              onIsDragging={handleIsDragging}
              dragIcon={customIcon}
              dragPreviewWrapperClass={styles.DragPreviewWrapper}
            >
              {/* Header with icon, title, category, and rating */}
              <div className={styles.CardHeader}>
                <div className={styles.IconContainer}>
                  {snippet.iconSvg ? (
                    <SvgFromString 
                      svgStr={snippet.iconSvg} 
                      width={55} 
                      height={55}
                    />
                  ) : (
                    <div className={styles.DefaultIcon}>
                      <Text strong>{snippet.title.charAt(0).toUpperCase()}</Text>
                    </div>
                  )}
                </div>
                <div className={styles.TitleSection}>
                  <div className={styles.TitleRow}>
                    <Text strong className={styles.Title}>
                      {snippet.title}
                    </Text>
                    <Tag color={snippet.category.color} className={styles.CategoryTag}>
                      {capitalize(snippet.category.name)}
                    </Tag>
                  </div>
                  <div className={styles.RatingSection}>
                    <Rate
                      value={snippet.rating?.average || 0}
                      allowHalf
                      onChange={(value) => handleRating(snippet.id, value)}
                      disabled={searchConfig.visibility === 'private'}
                      className={styles.InlineRating}
                    />
                    <Text type="secondary" className={styles.RatingCount}>
                      {snippet.rating?.total ? `(${snippet.rating.total})` : '(0)'}
                    </Text>

                    <VerticalSeparator />

                    <Space size="small" className={styles.DownloadsSection}>
                      <CloudDownloadOutlined className={styles.DownloadIcon} />
                      <Text type="secondary" className={styles.DownloadCount}>
                        {formatDownloadCount(snippet.downloads || 0)}
                      </Text>
                    </Space>

                    {/* Show visibility tag only when visibility is 'private' (my-snippets tab) */}
                    {searchConfig.visibility === 'private' && (
                      <>
                        <VerticalSeparator />
                        <Tag bordered={false} color={snippet.visibility === 'private' ? 'success' 
                          : snippet.visibility === 'organization' ? 'warning' : 'red'} className={styles.VisibilityTag} icon={visibilityTag.icon}>
                          {visibilityTag.text}
                        </Tag>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              <Paragraph
                className={styles.Description}
                ellipsis={{ rows: 2, expandable: false }}
              >
                {snippet.shortDescription}
              </Paragraph>

              {/* Tags and Author */}
              <div className={styles.CardFooter}>         
                {/* Author */}
                <Space size="small" className={styles.AuthorInfo}>
                  <Avatar size="small" icon={<UserOutlined />} />
                  <Text type="secondary" className={styles.Author}>
                    {t('Card.Author', { author: snippet.author?.name || 'Unknown' })}
                  </Text>
                </Space>


                {snippet.tags?.length > 0 && (
                  <VerticalSeparator />
                )}
                
                {/* Tags */}
                {snippet.tags && snippet.tags.length > 0 && (
                  <div className={styles.TagsContainer}>
                    <Space wrap size="small">
                      {snippet.tags.map((tag: string) => (
                        <Tag key={tag} className={styles.Tag}>
                          {tag}
                        </Tag>
                      ))}
                    </Space>
                  </div>
                )}
              </div>
            </Snippetify>
          </div>

          {/* Details Button */}
          <div className={styles.DetailsButtonContainer}>
            <StyledButton
              // type="primary"
              icon={<ArrowRightOutlined />}
              iconPosition='end'
              onClick={() => handleViewDetails(snippet)}
              className={styles.DetailsButton}
              noShadow
              title={t('Card.ViewDetails')}
            />
              
          </div>
        </div>
      </Card>
    );
  }

  const renderContent = () => {
    if (isLoading && searchResults.length === 0) {
      return (
        <div className={styles.LoadingContainer}>
          <Spin size="large" />
          <Text type="secondary" className={styles.LoadingText}>
            {t('Results.Loading')}
          </Text>
        </div>
      );
    }

    if (error) {
      return (
        <div className={styles.ErrorContainer}>
          <Empty
            description={error}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      );
    }

    if (searchResults.length === 0) {
      return (
        <div className={styles.EmptyContainer}>
          <Empty
            description={t('Results.NoResults')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      );
    }

    return (
      <div className={styles.ResultsContainer}>
        {/* Results count */}
        <div className={styles.ResultsHeader}>
          <Text type="secondary">
            {t('Results.Showing', { count: searchResults.length, total: totalResults })}
          </Text>
        </div>

        {/* Snippets grid */}
        <div className={styles.SnippetsGrid}>
          {searchResults.map(renderSnippetCard)}
        </div>

        {/* Load more button */}
        {hasMore && (
          <div className={styles.LoadMoreContainer}>
            <Button
              type="dashed"
              onClick={loadMore}
              loading={loadingMore}
              block
              className={styles.LoadMoreButton}
            >
              {loadingMore ? cw('Loading') : 'Load More'}
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.Container}>
      {renderContent()}
    </div>
  );
};


export default SnippetsList;
